import { Minus, Plus } from "lucide-react";

/**
 * Input seguro para número de copias.
 *
 * Problemas que resuelve:
 *  - Escribir "2" mientras hay "1" producía "12" porque el campo concatenaba
 *    sin seleccionar. Aquí onFocus selecciona todo automáticamente.
 *  - Sin botones +/-, era difícil de usar en móvil con teclado pequeño.
 *  - El usuario podía meter texto, decimales o negativos.
 *
 * Props:
 *   value (number, required)
 *   onChange (function(number))
 *   min  (default 1)
 *   max  (default 999)
 *   testid (string, optional) — prefijo para data-testid
 */
export default function CopiesInput({
  value,
  onChange,
  min = 1,
  max = 999,
  testid = "copies",
}) {
  const clamp = (n) => Math.max(min, Math.min(max, n));
  const v = Number.isFinite(Number(value)) ? Number(value) : min;

  const setSafe = (n) => {
    if (!Number.isFinite(n)) return onChange(min);
    onChange(clamp(Math.floor(n)));
  };

  return (
    <div
      className="flex items-center gap-2"
      data-testid={`${testid}-wrap`}
    >
      <button
        type="button"
        aria-label="Restar una copia"
        data-testid={`${testid}-dec`}
        onClick={() => setSafe(v - 1)}
        disabled={v <= min}
        className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-slate-700 disabled:opacity-40"
      >
        <Minus className="h-5 w-5" />
      </button>
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        min={min}
        max={max}
        step={1}
        value={String(v)}
        data-testid={`${testid}-input`}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return; // permite borrar mientras escribe
          const n = parseInt(raw, 10);
          if (!Number.isFinite(n)) return;
          setSafe(n);
        }}
        onBlur={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isFinite(n)) setSafe(min);
          else setSafe(n);
        }}
        className="h-12 w-20 rounded-xl border-2 border-slate-200 bg-white text-center text-xl font-black text-slate-900 focus:border-[#4d7c0f] focus:outline-none"
      />
      <button
        type="button"
        aria-label="Sumar una copia"
        data-testid={`${testid}-inc`}
        onClick={() => setSafe(v + 1)}
        disabled={v >= max}
        className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#4d7c0f] bg-[#4d7c0f] text-white disabled:opacity-40"
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}
